import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { toArray } from "../sports/tickerSportUtils";
import { NEWS_ROTATION_INTERVAL } from "../../constants-ticker";

const getNewsKey = (article, index) => article?.url || article?.title || `news-${index}`;

/** Trunca el texto a 50 caracteres y agrega puntos suspensivos si es más largo. */
const truncateHeadline = (text, maxLength = 210) => {
  if (text.length > maxLength) {
    return text.substring(0, maxLength) + "...";
  }
  return text;
};

/** Tarjeta individual de noticia: imagen a la izquierda y titular a la derecha. */
function NewsCard({ article }) {
  const headline = article?.title || "";
  const image = article?.image || "";
  const truncatedHeadline = truncateHeadline(headline);

  return (
    <div className="ticker-news-card">
      <p className="ticker-news-card__text">{truncatedHeadline}</p>
    </div>
  );
}

/**
 * Renderiza `news` con desplazamiento vertical: muestra una noticia a la vez y
 * la reemplaza deslizándola hacia arriba, a diferencia del resto del ticker que
 * avanza horizontalmente.
 */
export default function TickerNewsContent({ news, isActive = true }) {
  const articles = toArray(news).filter(Boolean);

  const [activeIndex, setActiveIndex] = useState(0);
  const [availableWidth, setAvailableWidth] = useState(0);
  const carouselRef = useRef(null);
  const articlesKey = articles.map(getNewsKey).join("|");

  // Duración de la animación de entrada del slide (debe coincidir con el
  // `transition.duration` del motion.div de abajo).
  const SLIDE_ENTER_MS = 450;

  // Iguala el ancho del carrusel al del viewport del ticker para que la noticia
  // ocupe toda la franja mientras rota verticalmente.
  useLayoutEffect(() => {
    if (!carouselRef.current) return undefined;

    const wrapper = carouselRef.current.closest(".ticker-content-wrapper");
    if (!wrapper) return undefined;

    const updateWidth = () => setAvailableWidth(wrapper.getBoundingClientRect().width);
    updateWidth();

    if (typeof ResizeObserver === "undefined") return undefined;

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(wrapper);
    return () => resizeObserver.disconnect();
  }, []);

  // Reinicia el carrusel al índice 0 cuando el bloque se activa/desactiva o
  // cambia el conjunto de noticias. NO se resetea por cambios de `isActive`: el
  // padre puede alternar `isActive` (p. ej. por remediciones del layout mientras
  // el carrusel rota), y reiniciar a 0 en cada cambio dejaría la noticia
  // congelada en el primer elemento.
  useEffect(() => {
    setActiveIndex(0);
  }, [articlesKey]);

  // Programa el avance de la noticia visible. El tiempo de permanencia empieza a
  // contar una vez que el elemento llegó a su posición final: por eso se suma la
  // duración de la animación de entrada (SLIDE_ENTER_MS) al primer retardo.
  // El efecto se re-ejecuta con cada cambio de activeIndex, así que cada noticia
  // dispone de su propio ciclo completo antes de dar paso a la siguiente.
  useEffect(() => {
    if (!isActive || articles.length < 2) return undefined;

    const timer = window.setTimeout(() => {
      setActiveIndex((currentIndex) => (currentIndex + 1) % articles.length);
    }, SLIDE_ENTER_MS + NEWS_ROTATION_INTERVAL);

    return () => window.clearTimeout(timer);
  }, [activeIndex, isActive, articles.length, articlesKey]);

  if (!articles.length) return null;

  const activeArticle = articles[activeIndex];

  return (
    <div
      ref={carouselRef}
      className="ticker-news-carousel"
      style={availableWidth ? { width: `${availableWidth}px`, flexBasis: `${availableWidth}px` } : undefined}
    >
      <AnimatePresence initial={false}>
        <motion.div
          key={getNewsKey(activeArticle, activeIndex)}
          className="ticker-news-carousel__slide"
          initial={{ y: "100%", opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: "-100%", opacity: 0 }}
          transition={{ duration: 0.45, ease: "easeInOut" }}
        >
          <NewsCard article={activeArticle} />
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
